import { Drawer } from 'antd'
import { Nfc, Trash2 } from 'lucide-react'
import { AppButton } from './AppButton'
import { BRAND } from '@/constants/brand'
import type { WizardLine } from '@/types/wizard'

export type LineTagDetailDrawerProps = {
  open: boolean
  line: WizardLine | null
  tags: string[]
  statusLabel?: string
  requiredQty?: number
  scanQty?: number
  nonScanQty?: number
  onClose: () => void
  onRemoveTag: (tag: string) => void
}

export function LineTagDetailDrawer({
  open,
  line,
  tags,
  statusLabel,
  requiredQty,
  scanQty,
  nonScanQty,
  onClose,
  onRemoveTag,
}: LineTagDetailDrawerProps) {
  if (!line) return null

  const title = line.materialDesc?.trim() || line.materialCode

  return (
    <Drawer
      title={null}
      placement="right"
      width={Math.min(480, typeof window !== 'undefined' ? window.innerWidth - 24 : 480)}
      open={open}
      onClose={onClose}
      destroyOnClose
      styles={{ body: { padding: 0 } }}
    >
      <div className="flex h-full flex-col">
        <div
          className="border-b border-border px-5 py-4"
          style={{ background: 'var(--leap-blush)' }}
        >
          <div className="text-[11px] font-extrabold uppercase tracking-wide text-[var(--leap-brand)]">
            Line scan detail
          </div>
          <div className="mt-1 text-[16px] font-black leading-5 text-foreground">{title}</div>
          <div className="mt-1 font-mono text-[12px] font-bold text-muted-foreground">
            {line.materialCode}
            {line.itemNo ? ` · Item ${line.itemNo}` : ''}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {statusLabel ? (
              <span className="rounded-full border border-border bg-card px-2.5 py-1 text-[10px] font-extrabold uppercase">
                {statusLabel}
              </span>
            ) : null}
            {requiredQty != null ? (
              <span className="rounded-full border border-border bg-card px-2.5 py-1 text-[10px] font-extrabold">
                Req {requiredQty}
              </span>
            ) : null}
            {scanQty != null ? (
              <span
                className="rounded-full border px-2.5 py-1 text-[10px] font-extrabold"
                style={{ borderColor: `${BRAND}33`, color: BRAND, background: `${BRAND}10` }}
              >
                Scanned {scanQty}
              </span>
            ) : null}
            {nonScanQty != null ? (
              <span className="rounded-full border border-border bg-card px-2.5 py-1 text-[10px] font-extrabold">
                Non-scan {nonScanQty}
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div className="text-[12px] font-extrabold uppercase tracking-wide text-muted-foreground">
            RFID tags · {tags.length}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {tags.length === 0 ? (
            <div className="rounded-[12px] border border-dashed border-border bg-muted/50 px-4 py-10 text-center text-[13px] font-semibold text-muted-foreground">
              No tags allocated to this line yet.
            </div>
          ) : (
            <ul className="space-y-2">
              {tags.map((tag, idx) => (
                <li
                  key={tag}
                  className="flex items-center gap-3 rounded-[12px] border border-border bg-card px-3 py-2.5 shadow-sm"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[rgba(175, 0, 0,0.08)] text-[var(--leap-brand)]">
                    <Nfc size={16} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-extrabold uppercase tracking-wide text-muted-foreground">
                      Tag {idx + 1}
                    </div>
                    <code className="block truncate font-mono text-[12px] font-bold text-foreground">
                      {tag}
                    </code>
                  </div>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-[11px] font-extrabold text-red-600 hover:bg-red-50"
                    onClick={() => onRemoveTag(tag)}
                  >
                    <Trash2 size={14} />
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-border p-4">
          <AppButton variant="outline" block onClick={onClose}>
            Close
          </AppButton>
        </div>
      </div>
    </Drawer>
  )
}
